import React, { createContext, Dispatch, SetStateAction, useContext, useState } from "react";
import { Const, User } from "snudbud-backbone-sdk";
import { LanguageChoice, OnboardingData } from "../types/onboarding";
import { useAPI } from "./APIContext";

interface OnboardingContextType {
  data: OnboardingData;
  setData: Dispatch<SetStateAction<OnboardingData>>;
  setLanguagesToLearn: (languages: LanguageChoice[]) => void;
  setKnownLanguages: (languages: Const.LanguageCode[]) => void;
  setStudyGoal: (goal: Const.DailyGoal) => void;
  setInterests: (interests: string[]) => void;
  setDescription: (description: string) => void;
  setName: (name: string) => void;
  setProfileImage: (image: File | null) => void;
  submitOnboarding: () => Promise<void>;
}

const defaultData = (user?: User.User): OnboardingData => ({
  description: "",
  languagesToLearn: [],
  knownLanguages: [],
  dailyGoal: "medium",
  interests: [],
  name: user?.name ?? "",
  profileImage: null,
});

const OnboardingContext = createContext<OnboardingContextType | null>(null);

export const OnboardingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { api, updateUser, user } = useAPI();
  const [data, setData] = useState<OnboardingData>(defaultData(user));

  const setLanguagesToLearn = (languages: LanguageChoice[]) =>
    setData((prev) => ({ ...prev, languagesToLearn: languages }));

  const setKnownLanguages = (languages: Const.LanguageCode[]) =>
    setData((prev) => ({ ...prev, knownLanguages: languages }));

  const setStudyGoal = (goal: Const.DailyGoal) => setData((prev) => ({ ...prev, dailyGoal: goal }));

  const setInterests = (interests: string[]) => setData((prev) => ({ ...prev, interests }));

  const setName = (name: string) => setData((prev) => ({ ...prev, name }));

  const setDescription = (description: string) => setData((prev) => ({ ...prev, description }));

  const setProfileImage = (image: File | null) => setData((prev) => ({ ...prev, profileImage: image }));

  const submitOnboarding = async () => {
    try {
      const { dailyGoal, description, interests, name } = data;
      await api.user.update({ dailyGoal, description, interests, name });

      if (data.profileImage) await api.user.updateProfileImage({ file: data.profileImage });

      for (const { languageCode, proficiency } of data.languagesToLearn)
        await api.user.language.create({ languageCode, proficiency, target: true });
      for (const languageCode of data.knownLanguages)
        await api.user.language.create({ languageCode, proficiency: 1, target: false });

      await updateUser(true);

      return Promise.resolve();
    } catch (error) {
      console.error("Error submitting onboarding data:", error);
      return Promise.reject(error);
    }
  };

  return (
    <OnboardingContext.Provider
      value={{
        data,
        setData,
        setLanguagesToLearn,
        setKnownLanguages,
        setStudyGoal,
        setDescription,
        setInterests,
        setName,
        setProfileImage,
        submitOnboarding,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) throw new Error("useOnboarding must be used within an OnboardingProvider");
  return context;
};
